import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { confirmDelete } from "@/lib/confirm";
import { toast } from "sonner";
import { Loader2, KeyRound, Download, Copy, Trash2, Check, Bot, Laptop } from "lucide-react";

const PROMPT = "Check Mission Control for due AI-agent tasks. Work through every due task: read it with get_agent_task, claim it, do the work exactly as instructed and deliver it (or post questions if you're genuinely blocked). Then give me a short report of what you delivered, what's waiting on answers and what's coming up next.";
const DOC_PROMPT = "Open the Mission Control doc [doc code or name] with get_doc. Action every open comment and the latest flow feedback with targeted edit_doc changes, reply to each comment saying what you changed, then submit_doc_for_review. Tell me what you changed.";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "Never");

function CopyBox({ text, testid }) {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); };
  return (
    <div className="flex items-start gap-2 rounded-xl bg-[#06060C] text-white px-3.5 py-2.5">
      <code data-testid={testid} className="font-mono-data text-xs break-all flex-1 leading-relaxed">{text}</code>
      <button onClick={copy} data-testid={`${testid}-copy`} className="text-white/70 hover:text-white shrink-0 mt-0.5">{done ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-4">
      <span className="h-7 w-7 rounded-full bg-[#EDE7FF] text-[#5b2fd6] font-mono-data text-xs font-semibold flex items-center justify-center shrink-0">{n}</span>
      <div className="flex-1 min-w-0 pb-6"><p className="font-display font-semibold text-[#06060C] mb-2">{title}</p>{children}</div>
    </div>
  );
}

export function ClaudeDesktopSettings() {
  const [keys, setKeys] = useState(null);
  const [name, setName] = useState("Agent laptop");
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/console/mcp/keys").then(({ data }) => setKeys(data.keys)).catch(() => setKeys([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setBusy("key");
    try { const { data } = await api.post("/console/mcp/keys", { name }); setNewKey(data.key); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const revoke = async (k) => {
    if (!(await confirmDelete({ title: "Revoke key?", message: `Claude Desktop using “${k.name}” will stop working immediately.`, confirmText: "Revoke" }))) return;
    await api.delete(`/console/mcp/keys/${k.id}`); toast.success("Key revoked"); load();
  };
  const download = async () => {
    setBusy("dl");
    try {
      const res = await api.get("/console/mcp/extension", { params: { base: window.location.origin }, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "mission-control.mcpb"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); } finally { setBusy(null); }
  };
  return (
    <div className="space-y-6" data-testid="claude-desktop-settings">
      <div className="rounded-3xl border border-[#EEEFEF] bg-white p-6">
        <div className="flex items-start gap-3 mb-6">
          <span className="h-10 w-10 rounded-2xl bg-[#06060C] text-white flex items-center justify-center shrink-0"><Laptop className="h-5 w-5" /></span>
          <div><h2 className="font-display text-xl font-semibold text-[#06060C]">Claude Desktop</h2><p className="text-sm text-[#595D62] mt-0.5">Connect Claude on your agent laptop so it works the AI-agent tasks and reports back to you.</p></div>
        </div>
        <Step n="1" title="Create a super admin key">
          {newKey ? (
            <div className="space-y-2" data-testid="mcp-new-key">
              <p className="text-xs text-amber-700">Copy this key now. It won't be shown again.</p>
              <CopyBox text={newKey} testid="mcp-new-key-value" />
              <button onClick={() => setNewKey(null)} className="text-xs text-[#723FFF] hover:underline">Done</button>
            </div>
          ) : (
            <div className="flex gap-2 max-w-md">
              <Input data-testid="mcp-key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name" className="bg-[#F6F6FD] border-[#EEEFEF]" />
              <Button data-testid="mcp-create-key" onClick={create} disabled={busy === "key" || !name.trim()} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full shrink-0">{busy === "key" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1.5" /> Create key</>}</Button>
            </div>
          )}
          {keys && keys.length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="mcp-keys">
              {keys.map((k) => (
                <div key={k.id} data-testid={`mcp-key-${k.id}`} className="flex items-center gap-3 rounded-xl border border-[#EEEFEF] px-3 py-2 text-sm">
                  <KeyRound className="h-3.5 w-3.5 text-[#9FA1A4]" /><span className="font-medium text-[#06060C]">{k.name}</span>
                  <span className="font-mono-data text-[11px] text-[#9FA1A4]">{k.prefix}…</span>
                  <span className="text-[11px] text-[#9FA1A4] ml-auto">Last used {fmt(k.last_used_at)}</span>
                  <button data-testid={`mcp-revoke-${k.id}`} onClick={() => revoke(k)} className="text-[#c4c5cc] hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </Step>
        <Step n="2" title="Download and install the extension">
          <Button data-testid="mcp-download" variant="outline" onClick={download} disabled={busy === "dl"} className="rounded-full border-[#C9B8FF] text-[#5b2fd6]">{busy === "dl" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> Download mission-control.mcpb</>}</Button>
          <p className="text-xs text-[#595D62] mt-2 leading-relaxed">On the laptop, open Claude Desktop → <span className="font-medium">Settings → Extensions</span>, drag the file in (or <span className="font-medium">Advanced settings → Install Extension</span>), paste the key when asked, and make sure the extension is switched on.</p>
        </Step>
        <Step n="3" title="Switch agents to Claude Desktop">
          <p className="text-xs text-[#595D62] leading-relaxed flex items-start gap-1.5"><Bot className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#723FFF]" /> In <span className="font-medium">Settings → AI Agents</span>, open an agent and set <span className="font-medium">Worked by</span> to <span className="font-medium">Claude Desktop</span>. The platform stops auto-running that agent and leaves its tasks for the laptop.</p>
        </Step>
        <Step n="4" title="Ask Claude to work the queue">
          <p className="text-xs text-[#595D62] mb-2">Paste this into a new Claude chat whenever you want a work session:</p>
          <CopyBox text={PROMPT} testid="mcp-prompt" />
          <p className="text-[11px] text-[#9FA1A4] mt-2">Deliveries use the same flow as the built-in agents: the doc is saved, the task is marked Done with time logged, a review task is raised and the Google Chat alert is sent. Claude can also answer questions like “what's overdue for Demo Co?”.</p>
        </Step>
        <Step n="5" title="Docs and sheets">
          <p className="text-xs text-[#595D62] leading-relaxed mb-2">Claude can read and edit any doc or sheet. Content flow draft and rework steps for Claude Desktop agents show up in its queue automatically: it edits the doc, replies to and resolves each comment, then sends the doc to the main task owner for review. A version is saved before every change, so anything can be restored from History. To work one doc directly, paste:</p>
          <CopyBox text={DOC_PROMPT} testid="mcp-doc-prompt" />
          <p className="text-[11px] text-[#9FA1A4] mt-2">From version 1.2.0 the extension picks up new Mission Control tools by itself. If you installed an earlier version, download and install it again once (replace the old one).</p>
        </Step>
      </div>
    </div>
  );
}
